import { useQuery, useQueryClient } from 'react-query';

import type { IProperty } from 'src/services/api/properties/types';
import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import { GET_PROPERTIES_PATH } from 'src/services/api/properties/constants';
import { normalizeOptions } from 'src/services/api/properties/use-property';

type IGetPropertiesOptions<TSelected = IProperty[]> = {
    disabled?: boolean;
    select?: (data: IProperty[]) => TSelected;
}

export type IGetPropertiesResponse = {
    properties: Array<IProperty>;
}

export const useProperties = <TSelected = IProperty[]>(options?: IGetPropertiesOptions<TSelected>) => {
    const queryClient = useQueryClient();

    return useQuery(
        [queryKeys.properties],
        async () => {
            /*
             * Facts ride along so client-side matching over the whole inventory (the
             * similar-properties pet filter reads facts.pet_policy) has something to
             * read; without them that filter silently never applies.
             */
            const res = await apiClient(`${GET_PROPERTIES_PATH}?include_facts=true`)
                .json<IGetPropertiesResponse>();
            return res.properties;
        }, {
            enabled: !options?.disabled,
            select: options?.select,
            onSuccess: (data) => {
                // make sure cache key aligns with use-property
                const normOptions = normalizeOptions(undefined);

                if (Array.isArray(data)) {
                    (data as IProperty[]).forEach((property) => {
                        queryClient.setQueryData<IProperty>([queryKeys.properties, property.id, normOptions], property);
                    });
                }
            },
        }
    );
};
